/**
 * Naming a cable the way the shop floor names it.
 *
 * "CYYF" is a family, not a product — the same name covers a dozen cables that
 * are nothing like each other to make. What identifies one is the family plus
 * how many cores and how thick:
 *
 *     CYYF 3 x 2.5
 *
 * And when the earth core is a smaller section than the rest, that is part of
 * the name too, written the way it is written on the drum label:
 *
 *     CYYF 3 x 25 + 16
 *
 * The "+ 16" is the green-yellow. It is not an afterthought or an annotation —
 * a 3 x 25 + 16 and a 3 x 25 are different cables with different bills of
 * material, and a screen that shows both as "CYYF" is asking someone to guess.
 */

const trim = n => {
  // Number(null) is 0, which is finite — so a missing green-yellow section came
  // out as "+ 0" and named a cable that does not exist. A section is a positive
  // number or it is not there.
  if (n == null || n === '') return null;
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return null;
  // 2.5 stays 2.5; 25.0 becomes 25
  return String(Number(v.toFixed(3)));
};

/** "3 x 25 + 16", or "3 x 2.5" when every core is the same section. */
export function cableSpec(product) {
  const cores = Number(product?.numberOfConductors) || 0;
  const section = trim(product?.section);
  if (!cores || section == null) return '';

  const mixed = Boolean(product?.hasMixedConductorSections);
  const gy = mixed ? trim(product?.greenYellowSection) : null;

  return `${cores} x ${section}${gy ? ` + ${gy}` : ''}`;
}

/** "CYYF 3 x 25 + 16" — everything needed to know which cable this is. */
export function cableLabel(product, fallback = '') {
  const name = String(product?.cableName || fallback || '').trim();
  const spec = cableSpec(product);
  return [name, spec].filter(Boolean).join(' ') || fallback || '';
}
